import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Sparkles, Award, Calendar, Users, CalendarClock } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

const tierNames = {
  basic: 'Seekers Community Membership',
  gold: 'Yearly Gold Member',
  diamond: '5-Year Diamond Member',
  lifetime: 'Lifetime Residential',
};

const DashboardHome = () => {
  const { user, hasRole } = useAuth();
  const [myTeachers, setMyTeachers] = useState(null);
  const [planMap, setPlanMap] = useState({});
  const isFacilitator = hasRole('facilitator') || hasRole('ashram_coord') || hasRole('center_coord');
  useEffect(() => {
    if (isFacilitator) api.get('/teachers/mine').then((r) => setMyTeachers(r.data || [])).catch(() => setMyTeachers([]));
  }, [isFacilitator]);
  // Map each plan's tier_key -> its (admin-renamed) display name so we never show stale hardcoded labels.
  useEffect(() => {
    api.get('/memberships/plans')
      .then((r) => setPlanMap(Object.fromEntries((r.data || []).map((p) => [p.tier_key, p.name]))))
      .catch(() => {});
  }, []);
  const ownsProfile = (myTeachers || []).some((t) => t.user_id);
  const hasApprovedOffer = (myTeachers || []).some((t) => t.offers_private_lessons && t.private_lesson_status === 'approved');
  const tier = user?.membership?.tier;
  const membershipLabel = planMap[tier] || user?.membership?.plan_name || tierNames[tier] || 'Seekers Community Membership';

  return (
    <div className="space-y-8">
      <section>
        <div className="eyebrow mb-2">Welcome back</div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy">
          Namaste, <span className="font-italic-display text-rose-italic">{user?.full_name?.split(' ')[0]}</span>
        </h1>
        <p className="mt-2 text-mahogany/80 max-w-xl">Your journey continues here — track retreats, manage your profile, and stay connected with the community.</p>
      </section>

      <section className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={Award} label="Membership" value={membershipLabel} accent />
        <Card icon={Sparkles} label="Karma Credits" value={user?.karma_credits?.balance ?? 0} />
        <Card icon={Calendar} label="Events Booked" value={0} muted />
        <Card icon={Users} label="Roles" value={(user?.roles || []).length} />
      </section>

      {(!user?.membership?.tier || user?.membership?.tier === 'basic') && (
        <section className="p-6 rounded-2xl border border-mahogany/30 bg-mahogany/5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-serif-display text-2xl text-burgundy">Deepen your journey</h3>
            <p className="mt-1 text-mahogany/80 max-w-xl">Upgrade your membership for retreat discounts, free stays, and more.</p>
          </div>
          <Link to="/membership" className="pill-btn">View Membership Plans</Link>
        </section>
      )}

      {isFacilitator && ownsProfile && (
        <section className="p-6 rounded-2xl border border-mahogany/30 bg-mahogany/5" data-testid="offer-private-hint">
          <div className="flex items-start gap-3">
            <CalendarClock className="w-6 h-6 text-rose-italic shrink-0 mt-0.5" />
            <div>
              <h3 className="font-serif-display text-2xl text-burgundy">{hasApprovedOffer ? 'Your private sessions are live' : 'Offer private sessions'}</h3>
              <p className="mt-2 text-mahogany/80 max-w-2xl">
                {hasApprovedOffer
                  ? 'Manage your rate, availability and incoming booking requests — accept, decline or propose a new time.'
                  : 'Offer 1:1 consultations or private meditation classes — free or paid. Set your rate and availability, sign the facilitator agreement, and your offer goes to an admin for approval.'}
              </p>
              <Link to="/dashboard/consultations" className="pill-btn mt-4 inline-flex" data-testid="offer-private-cta">{hasApprovedOffer ? 'Manage bookings' : 'Set up private sessions'}</Link>
            </div>
          </div>
        </section>
      )}

      {!hasRole('facilitator') && !hasRole('admin') && (
        <section className="p-6 rounded-2xl border border-rose/50 bg-rose-soft/30">
          <h3 className="font-serif-display text-2xl text-burgundy">Become a Facilitator</h3>
          <p className="mt-2 text-mahogany/80 max-w-2xl">Share your teachings with the community. Submit your profile for admin review — once approved, you’ll appear on our Facilitators &amp; Teachers page.</p>
          <Link to="/dashboard/facilitator" className="pill-btn mt-4 inline-flex">Submit Facilitator Profile</Link>
        </section>
      )}

      {!hasRole('ashram_coord') && !hasRole('center_coord') && !hasRole('admin') && (
        <section className="p-6 rounded-2xl border border-rose/50 bg-white/70">
          <h3 className="font-serif-display text-2xl text-burgundy">Are you an Ashram or Center Coordinator?</h3>
          <p className="mt-2 text-mahogany/80 max-w-2xl">Coordinators can add retreats, programs, and facilitators for their ashram or center. All submissions are reviewed by an admin.</p>
          <Link to="/dashboard/coordinator" className="pill-btn mt-4 inline-flex">Request Coordinator Access</Link>
        </section>
      )}

      {hasRole('admin') && (
        <section className="p-6 rounded-2xl border border-mahogany/30 bg-mahogany/5">
          <h3 className="font-serif-display text-2xl text-burgundy">Admin Tools</h3>
          <p className="mt-2 text-mahogany/80">You have full platform access. Manage users, approve facilitators &amp; coordinators, and oversee approvals.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/dashboard/admin" className="pill-btn">Open Admin Dashboard</Link>
            <Link to="/dashboard/admin/approvals" className="pill-btn-outline">Approval Queue</Link>
          </div>
        </section>
      )}
    </div>
  );
};

const Card = ({ icon: Icon, label, value, accent, muted }) => (
  <div className={`p-5 rounded-2xl border ${accent ? 'border-mahogany/30 bg-mahogany/5' : 'border-rose/50 bg-white/70'}`}>
    <div className="flex items-center gap-2 text-mahogany/80 text-xs uppercase tracking-widest">
      <Icon className="w-4 h-4" /> {label}
    </div>
    <div className={`mt-3 font-serif-display text-3xl ${muted ? 'text-burgundy/60' : 'text-burgundy'}`}>{value}</div>
  </div>
);

export default DashboardHome;
